import '@mantine/core/styles.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@mantine/notifications/styles.css';
import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { createAppKit } from '@reown/appkit/react';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { WagmiProvider, fallback, http } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { rangeFromZero } from 'src/utils';
import { NETWORK_INFO, RECOMMEND_HAHA_WALLET } from 'src/constants';
import { Router } from './Router';
import { theme } from './theme';
import './global.scss';

declare global {
  interface Window {
    phantom: any;
    haha: any;
  }
}

const projectId = import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID || '';

const metadata = {
  name: 'aPriori Liquid Staking',
  description: 'MEV-powered liquid staking on Monad',
  url: 'https://web3modal.com',
  icons: ['https://avatars.githubusercontent.com/u/37784886'],
};

const wagmiAdapter = new WagmiAdapter({
  networks: [NETWORK_INFO],
  projectId,
  transports: {
    [NETWORK_INFO.id]: fallback(
      NETWORK_INFO.rpcUrls.default.http.map((url) =>
        http(url, { batch: { wait: 100 }, retryCount: 0 })
      )
    ),
  },
});

createAppKit({
  adapters: [wagmiAdapter],
  networks: [NETWORK_INFO],
  metadata,
  projectId,
  themeMode: 'dark',
  themeVariables: {
    '--w3m-z-index': 1000,
  },
  features: {
    analytics: false,
    email: false,
    socials: false,
    swaps: false,
    onramp: false,
    send: false,
    history: false,
  },
  featuredWalletIds: [
    'a797aa35c0fadbfc1a53e7f675162ed5226968b44a19ee3d24385c64d1d3c393', // phantom
    '971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709', // okx
    '2bd8c14e035c2d48f184aaa168559e86b0e3433228d3c4075900a221785019b0', // backpack
    ...(RECOMMEND_HAHA_WALLET && !window.haha
      ? ['719bd888109f5e8dd23419b20e749900ce4d2fc6858cf588395f19c82fd036b3']
      : []),
  ],
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 30,
      retry: false,
    },
  },
});

export default function App() {
  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <MantineProvider
          theme={theme}
          cssVariablesResolver={() => {
            return {
              light: {},
              dark: {},
              variables: {
                ...rangeFromZero(10).reduce(
                  (acc, i) => {
                    acc[`--mantine-color-main-${i}`] =
                      `var(--mantine-color-dark-${i})`;
                    return acc;
                  },
                  {} as Record<string, string>
                ),
              },
            };
          }}
          forceColorScheme="dark"
        >
          <ModalsProvider>
            <Notifications />
            <Router />
          </ModalsProvider>
        </MantineProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}
